/**
 * Chart mapping — each company file's QuickBooks accounts onto Sixgen's canonical lines.
 * Phase 5, task 5.7; `apps/api/src/routes/chart.ts`.
 *
 * An unmapped account's lines sit in PENDING CLASSIFICATION beside every statement, never inside
 * it, so this screen is what turns a connected company file into a P&L. Accounts are grouped by
 * name and type across company files: fifteen LLCs on similar charts means "Cleaning & Janitorial"
 * is mapped once, not fifteen times. The suggestion beside each group is `proposeCanonicalKey`'s,
 * computed by the API; "Apply safe suggestions" writes only the unambiguous ones, and only onto
 * accounts nobody has mapped. A person's choice always wins, and the API refuses one that would
 * move money across types.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api.js';

interface CanonicalLine {
  key: string;
  name: string;
  statement_section: string;
  account_type: string;
}

interface ChartAccount {
  id: string;
  entityId: string;
  entityName: string;
  name: string;
  fullyQualifiedName: string | null;
  accountType: string;
  accountSubtype: string | null;
  canonicalKey: string | null;
  isActive: boolean;
  proposal: string | null;
}

interface ChartResponse {
  canonical: CanonicalLine[];
  accounts: ChartAccount[];
}

interface Group {
  key: string;
  name: string;
  accountType: string;
  accounts: ChartAccount[];
  /** The mapping every account in the group shares, or null when they differ or none has one. */
  mapped: string | null;
  mixed: boolean;
  proposal: string | null;
}

/** Same name (case-insensitive) and same type: one decision. */
function groupAccounts(accounts: ChartAccount[]): Group[] {
  const groups = new Map<string, Group>();
  for (const a of accounts) {
    const key = `${a.accountType}|${a.name.trim().toLowerCase()}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        name: a.name,
        accountType: a.accountType,
        accounts: [],
        mapped: null,
        mixed: false,
        proposal: null,
      };
      groups.set(key, g);
    }
    g.accounts.push(a);
  }
  for (const g of groups.values()) {
    const keys = new Set(g.accounts.map((a) => a.canonicalKey));
    g.mixed = keys.size > 1;
    g.mapped = keys.size === 1 ? [...keys][0]! : null;
    g.proposal = g.accounts.find((a) => a.proposal)?.proposal ?? null;
  }
  return [...groups.values()];
}

const TYPE_ORDER = ['income', 'expense', 'asset', 'liability', 'equity'];

export function ChartPage(): ReactNode {
  const queryClient = useQueryClient();
  const [show, setShow] = useState<'unmapped' | 'all'>('unmapped');
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const chart = useQuery({
    queryKey: ['finance', 'chart'],
    queryFn: () => api.get<ChartResponse>('/api/finance/chart'),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['finance'] });

  const applyProposals = useMutation({
    mutationFn: () =>
      api.post<{ mapped: number; unmapped: number }>('/api/finance/chart/apply-proposals', {}),
    onSuccess: (r) => {
      setError(null);
      setMessage(
        `Mapped ${r.mapped} account${r.mapped === 1 ? '' : 's'} from safe suggestions; ` +
          `${r.unmapped} still need${r.unmapped === 1 ? 's' : ''} you.`,
      );
      void refresh();
    },
    onError: (err: unknown) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const save = useMutation({
    mutationFn: (args: { group: Group; canonicalKey: string | null }) =>
      api.put<{ changed: number }>('/api/finance/chart/accounts', {
        accountIds: args.group.accounts.map((a) => a.id),
        canonicalKey: args.canonicalKey,
      }),
    onSuccess: (r, args) => {
      setError(null);
      setMessage(
        `"${args.group.name}": ${r.changed} account${r.changed === 1 ? '' : 's'} ` +
          (args.canonicalKey ? 'mapped.' : 'cleared.'),
      );
      void refresh();
    },
    onError: (err: unknown) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const data = chart.data;
  const linesByType = useMemo(() => {
    const m = new Map<string, CanonicalLine[]>();
    for (const c of data?.canonical ?? []) {
      m.set(c.account_type, [...(m.get(c.account_type) ?? []), c]);
    }
    return m;
  }, [data]);
  const lineName = useMemo(
    () => new Map((data?.canonical ?? []).map((c) => [c.key, c.name])),
    [data],
  );

  const groups = useMemo(() => {
    const all = groupAccounts((data?.accounts ?? []).filter((a) => a.isActive));
    return all
      .filter((g) => show === 'all' || g.mapped === null)
      .sort(
        (a, b) =>
          TYPE_ORDER.indexOf(a.accountType) - TYPE_ORDER.indexOf(b.accountType) ||
          b.accounts.length - a.accounts.length ||
          a.name.localeCompare(b.name),
      );
  }, [data, show]);

  const active = (data?.accounts ?? []).filter((a) => a.isActive);
  const unmapped = active.filter((a) => a.canonicalKey === null).length;
  const suggested = active.filter((a) => a.canonicalKey === null && a.proposal).length;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Chart mapping</h1>
          <p className="subtitle">
            Each QuickBooks account onto one of Sixgen&apos;s statement lines. Until an account is
            mapped, its money sits beside the statements as pending classification, not in them.
          </p>
        </div>
      </header>

      {chart.isLoading && <p className="muted">Loading…</p>}
      {chart.isError && <p className="error">Could not load the chart: {chart.error.message}</p>}
      {error && <p className="error">{error}</p>}
      {message && <div className="alert alert-ok">{message}</div>}

      {data && (
        <section className="card">
          <p>
            <strong>{unmapped}</strong> of {active.length} active accounts unmapped across{' '}
            {new Set(active.map((a) => a.entityId)).size} company files.{' '}
            {suggested > 0 && (
              <>
                {suggested} have a safe suggestion.{' '}
                <button
                  type="button"
                  disabled={applyProposals.isPending}
                  onClick={() => applyProposals.mutate()}
                >
                  {applyProposals.isPending ? 'Applying…' : 'Apply safe suggestions'}
                </button>
              </>
            )}
          </p>
          <div className="filters">
            <label>
              Show{' '}
              <select value={show} onChange={(e) => setShow(e.target.value as 'unmapped' | 'all')}>
                <option value="unmapped">Unmapped only</option>
                <option value="all">Everything</option>
              </select>
            </label>
          </div>

          {groups.length === 0 ? (
            <p className="empty">Nothing to map. Every active account has a statement line.</p>
          ) : (
            <table className="table compact">
              <thead>
                <tr>
                  <th>QuickBooks account</th>
                  <th>Type</th>
                  <th>Company files</th>
                  <th>Mapped to</th>
                  <th>Statement line</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const selected = choice[g.key] ?? g.mapped ?? g.proposal ?? '';
                  return (
                    <tr key={g.key}>
                      <td>{g.name}</td>
                      <td className="muted">{g.accountType}</td>
                      <td title={g.accounts.map((a) => a.entityName).join(', ')}>
                        {g.accounts.length === 1
                          ? g.accounts[0]!.entityName
                          : `${g.accounts.length} files`}
                      </td>
                      <td>
                        {g.mixed ? (
                          <span className="chip chip-warn">differs by file</span>
                        ) : g.mapped ? (
                          lineName.get(g.mapped)
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <select
                          aria-label={`Statement line for ${g.name}`}
                          value={selected}
                          onChange={(e) => setChoice({ ...choice, [g.key]: e.target.value })}
                        >
                          <option value="">Choose a line…</option>
                          {(linesByType.get(g.accountType) ?? []).map((c) => (
                            <option key={c.key} value={c.key}>
                              {c.name}
                              {c.key === g.proposal && g.mapped === null ? ' (suggested)' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          disabled={save.isPending || selected === '' || selected === g.mapped}
                          onClick={() => save.mutate({ group: g, canonicalKey: selected })}
                        >
                          {g.accounts.length > 1 ? `Map all ${g.accounts.length}` : 'Map'}
                        </button>
                        {g.mapped && !g.mixed && (
                          <button
                            type="button"
                            className="link-button"
                            disabled={save.isPending}
                            onClick={() => save.mutate({ group: g, canonicalKey: null })}
                          >
                            Clear
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
